// sheet.js — dauerhaftes (nicht modales) Bottom-Sheet mit Rastpunkten, gezogen per Federphysik.
//   const s = createStandardSheet(el, { snaps: (h) => [peek, half, full], initial: 1, onChange, followers })
//   s.snapTo(i) · s.index · s.destroy()
// Ziehen am Griff/Kopf; in der Liste erst, wenn sie oben ist (wie Google Maps).
// followers: Selektor für Elemente, die über dem Sheet schweben (Knöpfe, Legenden). Sie bekommen die sichtbare Höhe als
// --sheet-h direkt an sich selbst und verschieben sich per translate. Bewusst NICHT am Elternelement: eine geerbte
// Variable, die sich in jedem Bild ändert, zwingt Karte und Liste in jedem Bild zur Stilberechnung (gemessen ~46 fps).
import { spring, velocityTracker, rubberClamp, haptic } from '../motion/physics.js';

export function nearestSnap(y, v, snaps) {
  // y = sichtbare Höhe; v = Geschwindigkeit (px/s, positiv = nach oben); Schwung wählt die nächste Stufe in Richtung
  const projected = y + v * 0.18;
  let best = 0;
  for (let i = 1; i < snaps.length; i++) if (Math.abs(snaps[i] - projected) < Math.abs(snaps[best] - projected)) best = i;
  return best;
}

export function createStandardSheet(el, { snaps, initial = 1, onChange, followers = '', handle = el.querySelector('[data-sheet-drag]'), scroller = el.querySelector('[data-sheet-scroll]') } = {}) {
  const follow = () => (followers && el.parentElement ? el.parentElement.querySelectorAll(followers) : []);
  let H = 0, points = [], index = initial, visible = 0;
  const measure = () => {
    H = el.parentElement.clientHeight;
    points = snaps(H).map((p) => Math.max(0, Math.min(H, p)));
  };
  const apply = (h) => {
    visible = h;
    // Unterpixel: ganze CSS-Pixel sind auf dem Handy 3 Gerätepixel — das Auslaufen der Feder liefe in Stufen
    const v = h.toFixed(2) + 'px';
    for (const f of follow()) f.style.setProperty('--sheet-h', v);
    el.style.transform = `translateY(${(H - h).toFixed(2)}px)`;
    el.dataset.snap = String(index);
  };
  // Eine Feder für die sichtbare Höhe: ein neues Ziel übernimmt Stand und Schwung (auch mitten im Federn)
  const h = spring(0, { spring: 'spatial-default', precision: 0.5, onUpdate: (x) => apply(x) });
  const animateTo = (target, v0 = 0) => {
    if (h.value !== visible) h.jump(visible);
    h.to(target, { velocity: v0 });
  };
  const snapTo = (i, v0 = 0) => {
    measure();
    const prev = index;
    index = Math.max(0, Math.min(points.length - 1, i));
    el.dataset.snap = String(index);
    animateTo(points[index], v0);
    if (onChange && prev !== index) onChange(index);
  };

  // Ziehen
  let drag = null;
  const down = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const fromList = scroller && scroller.contains(e.target);
    if (fromList && scroller.scrollTop > 0) return; // Liste scrollt zuerst
    if (fromList && e.pointerType === 'mouse') return; // Maus zieht nur am Griff
    h.stop();
    drag = { y0: e.clientY, h0: visible, tr: velocityTracker(), moved: false, fromList, id: e.pointerId };
    drag.tr.add(e.timeStamp, visible);
  };
  const move = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dy = e.clientY - drag.y0;
    if (!drag.moved) {
      if (Math.abs(dy) < 6) return;
      // In der Liste nach oben wischen bei voller Höhe = scrollen, nicht ziehen
      if (drag.fromList && dy < 0 && index === points.length - 1) { drag = null; return; }
      drag.moved = true;
      el.setPointerCapture?.(e.pointerId);
    }
    e.preventDefault();
    const raw = drag.h0 - dy;
    drag.tr.add(e.timeStamp, raw);
    // Über den obersten bzw. unter den untersten Rastpunkt hinaus: Gummiband statt harter Grenze
    apply(rubberClamp(raw, points[0], points[points.length - 1], H || 1));
  };
  const up = (e) => {
    if (!drag || (e && e.pointerId !== drag.id)) return;
    const d = drag; drag = null;
    if (!d.moved) return;
    const v = e && e.type !== 'pointercancel' ? d.tr.velocity(e.timeStamp) : 0;
    const i = nearestSnap(visible, v, points);
    if (i !== index) haptic('snap');
    snapTo(i, v);
  };
  el.addEventListener('pointerdown', down);
  addEventListener('pointermove', move, { passive: false });
  addEventListener('pointerup', up);
  addEventListener('pointercancel', up);
  if (handle) {
    handle.addEventListener('click', () => snapTo(index === points.length - 1 ? 1 : index + 1));
    handle.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); snapTo(index + 1); }
      if (e.key === 'ArrowDown') { e.preventDefault(); snapTo(index - 1); }
    });
  }
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { measure(); apply(points[index]); }) : null;
  if (ro) ro.observe(el.parentElement);
  measure(); apply(points[index]);

  return {
    get index() { return index; },
    snapTo,
    destroy() {
      h.stop(); if (ro) ro.disconnect();
      el.style.transform = ''; delete el.dataset.snap;
      for (const f of follow()) f.style.removeProperty('--sheet-h');
      el.removeEventListener('pointerdown', down);
      removeEventListener('pointermove', move); removeEventListener('pointerup', up); removeEventListener('pointercancel', up);
    },
  };
}
